import {
  Component,
  computed,
  effect,
  OnDestroy,
  OnInit,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '@environments/environment';
import { CurrentUser } from '@models/Core/CurrentUser';
import { Session } from '@models/Sessions/Session';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import { ConfigService, IRoleSelectOption } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { matchRole, sortByDate } from '@utils/sessionFilters';
import { matchCreatedBy, matchFav } from '@utils/universalFilters';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';

import { NewArticleComponent } from './article/new-article.component';
import { NewCustomStyleComponent } from './custom-style/new-custom-style.component';
import { NewPodcastComponent } from './podcast/new-podcast.component';

@Component({
  standalone: true,
  imports: [
    NewPodcastComponent,
    NewArticleComponent,
    NewCustomStyleComponent,
    DividerModule,
    SelectModule,
    FormsModule,
    FloatLabelModule,
    ButtonModule,
    RouterModule,
    SkeletonModule,
  ],
  templateUrl: './new-project-page.component.html',
  styleUrl: './new-project-page.component.css',
})
export class NewProjectPageComponent implements OnInit, OnDestroy {
  featureFlags = environment.featureFlags;
  validProjectTypes = ['podcast', 'article', 'custom-style'] as const;
  projectType: (typeof this.validProjectTypes)[number] =
    this.validProjectTypes[0];
  topics: Session[] = null;
  selectedTopic: Session;
  contextTopic: string;
  isPending = true;
  sortReverse = true;
  filterOwn = false;
  filterFav = false;

  // User fields
  currentUser: CurrentUser;
  userRoles: IRoleSelectOption[] = [];

  // Child components
  newPodcastComponent = viewChild(NewPodcastComponent);
  newArticleComponent = viewChild(NewArticleComponent);
  newCustomStyleComponent = viewChild(NewCustomStyleComponent);

  // Create a computed signal that combines both states
  isGenerating = computed(() => {
    return [
      this.newPodcastComponent,
      this.newArticleComponent,
      this.newCustomStyleComponent,
    ].some(ref => {
      return ref()?.isGenerating();
    });
  });

  get filteredParticipating() {
    return this.topics
      .filter(s =>
        matchRole(s, this.appConfig.roles, 'SESSION_PARTICIPANT', '<='),
      )
      .filter(s =>
        matchCreatedBy(s, this.authService.currentUserSync._id, this.filterOwn),
      )
      .filter(s => matchFav(s, this.filterFav))
      .sort((s1, s2) => sortByDate(s1, s2, this.sortReverse));
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private bus: BusService,
    private events: EventsService,
    private appConfig: ConfigService,
    private messageService: MessageService,
    private authService: AuthenticationService,
  ) {
    this.userRoles = appConfig.rolesSelectOptions;
    effect(() => {
      this.isPending = this.isGenerating();
    });
  }

  ngOnInit() {
    this.subscribe();
    // Redirect to the first valid project type if the query param is invalid
    this.activatedRoute.queryParams.subscribe(params => {
      const { type, topic } = params;
      this.contextTopic = topic;

      // Redirect to article if the topic input is disabled and the type is podcast
      if (
        type === 'podcast' &&
        !this.featureFlags.projectCreation.topicInput &&
        !this.featureFlags.projectCreation.textInput
      ) {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { type: 'article' },
          replaceUrl: true,
        });
        return;
      }

      // Redirect to article if custom-style feature is toggled off
      if (
        type === 'custom-style' &&
        !this.featureFlags.projectCreation.customStyleInput
      ) {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { type: 'article' },
          replaceUrl: true,
        });
        return;
      }

      if (!type || !this.validProjectTypes.includes(type)) {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { type: this.validProjectTypes[0] },
          replaceUrl: true,
        });
        return;
      }
      this.projectType = type;
    });

    // Assuming the sessions are already fetched
    this.bus.publish(this.events.requested.data.session.all);
  }

  private updateSelectedTopic() {
    this.selectedTopic =
      // Use the context topic if it exists
      this.filteredParticipating.find(
        session => session._id === this.contextTopic,
      ) ||
      // Use the default session ID if it exists
      this.filteredParticipating.find(
        session =>
          session._id === this.authService.currentUserSync.default_session_id,
      ) ||
      // Fallback to the first session in the filtered list
      this.filteredParticipating[0];
  }

  private sessionsReceive(sessions: Session[]) {
    this.topics = sessions;
    this.updateSelectedTopic();
    this.isPending = false;
  }

  private handleError() {
    [
      this.newPodcastComponent,
      this.newArticleComponent,
      this.newCustomStyleComponent,
    ].forEach(ref => {
      return ref()?.isGenerating.set(false);
    });
    this.messageService.add({
      severity: 'error',
      summary: 'Something went wrong.',
      detail: 'Please try change input data or try later.',
      sticky: true,
    });
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.received.data.session.all.success,
      this.sessionsReceive,
      this,
    );
    this.bus.subscribe(
      this.events.received.action.session.idea.create.failure,
      this.handleError,
      this,
    );
  }

  private unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.data.session.all.success,
      this.sessionsReceive,
    );
    this.bus.unsubscribe(
      this.events.received.action.session.idea.create.failure,
      this.handleError,
    );
  }

  ngOnDestroy() {
    this.unsubscribe();
  }
}
